import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, KeyRound, Users, ListVideo, Plus, Trash2, Copy, Check,
  LogOut, Ban, CheckCircle2, RefreshCw, Eye,
} from "lucide-react";
import { Logo } from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "codes", label: "Access Codes", icon: KeyRound },
  { id: "users", label: "Users", icon: Users },
  { id: "lists", label: "Playlists", icon: ListVideo },
];

export default function AdminPanel() {
  const nav = useNavigate();
  const { adminLogout } = useAuth();
  const [tab, setTab] = useState("codes");

  const logout = () => {
    adminLogout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-tv-darkest flex">
      {/* Sidebar */}
      <aside className="w-[230px] shrink-0 border-r border-tv-border bg-tv-darkest flex flex-col p-4">
        <div className="px-2 py-3">
          <Logo size={22} />
        </div>
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-tv-orange bg-tv-orange/10 border border-tv-orange/25 rounded-full px-3 py-1 mb-6 mx-2 w-fit">
          <Shield size={12} /> Admin
        </div>
        <nav className="space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-tv-card text-tv-cyan border-l-[3px] border-tv-cyan" : "text-tv-sub hover:bg-tv-hover hover:text-tv-text"
              }`}
            >
              <t.icon size={17} />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto space-y-2">
          <button onClick={() => nav("/")} className="btn-primary w-full flex items-center justify-center gap-2 text-sm" data-testid="view-as-user-button">
            <Eye size={16} /> View as user
          </button>
          <ThemeToggle className="w-full" />
          <button onClick={logout} className="btn-ghost w-full flex items-center justify-center gap-2" data-testid="admin-logout-button">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-8 overflow-auto">
        {tab === "codes" && <CodesTab />}
        {tab === "users" && <UsersTab />}
        {tab === "lists" && <ListsTab />}
      </main>
    </div>
  );
}

function Header({ title, sub, children }) {
  return (
    <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
        <p className="text-tv-sub text-sm mt-1">{sub}</p>
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

function Stat({ label, value, color = "text-tv-cyan" }) {
  return (
    <div className="card px-5 py-4">
      <div className={`text-3xl font-extrabold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-tv-muted font-bold mt-1">{label}</div>
    </div>
  );
}

/* Admin-set password. The value is shown in clear on purpose: the admin has to read
   it back to the user, and masking a password you just typed for someone else only
   invites typos. It is never stored client-side and the dialog closes on success. */
function SetPasswordDialog({ user, onClose, onError }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const generate = () => {
    const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const buf = new Uint8Array(14);
    crypto.getRandomValues(buf);
    setPw([...buf].map((b) => chars[b % chars.length]).join(""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { onError("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { password: pw });
      setDone(true);
    } catch (err) {
      onError(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="set-password-dialog">
      <div className="card w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-tv-text mb-1 flex items-center gap-2">
          <KeyRound size={18} className="text-tv-cyan" /> Set a new password
        </h3>
        <p className="text-sm text-tv-muted mb-5 truncate">{user.email}</p>

        {done ? (
          <>
            <div className="text-sm text-tv-green bg-tv-green/10 border border-tv-green/30 rounded-xl px-4 py-3 mb-4">
              Password updated. Give the user this password, it is not shown again.
            </div>
            <div className="font-mono text-tv-text bg-tv-medium border border-tv-border rounded-xl px-4 py-3 mb-5 break-all select-all">{pw}</div>
            <button onClick={onClose} className="btn-primary w-full" data-testid="close-password-dialog">Done</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="flex gap-2 mb-4">
              <input
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="New password"
                className="field flex-1 font-mono"
                data-testid="new-password-input"
              />
              <button type="button" onClick={generate} className="btn-ghost px-3 text-sm whitespace-nowrap">Generate</button>
            </div>
            <p className="text-xs text-tv-muted mb-5">
              Minimum 6 characters. Any pending password-reset link for this account stops working.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary flex-1 disabled:opacity-60" data-testid="save-password-button">
                {busy ? "Saving…" : "Set password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* Inline note on an access code. Saves on blur or Enter, and only when the text
   actually changed, so tabbing through the table does not fire a write per row. */
function NoteCell({ code, onError }) {
  const [value, setValue] = useState(code.notes || "");
  const [state, setState] = useState("idle"); // idle | saving | saved
  const saved = useRef(code.notes || "");

  const save = async () => {
    if (value === saved.current) return;
    setState("saving");
    try {
      await api.patch(`/admin/codes/${code.id}`, { notes: value });
      saved.current = value;
      setState("saved");
      setTimeout(() => setState("idle"), 1500);
    } catch (e) {
      setState("idle");
      setValue(saved.current); // do not leave text on screen that was never stored
      onError(errMsg(e));
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setValue(saved.current); e.currentTarget.blur(); } }}
        placeholder="Nickname, note…"
        maxLength={500}
        className="bg-transparent border border-transparent hover:border-tv-border focus:border-tv-cyan/60 rounded-lg px-2 py-1 text-sm text-tv-sub focus:text-tv-text outline-none w-44 transition-colors"
        data-testid={`code-notes-${code.id}`}
      />
      {state === "saving" && <span className="text-[10px] text-tv-muted">saving…</span>}
      {state === "saved" && <Check size={13} className="text-tv-green" />}
    </div>
  );
}

/* ── Access Codes ─────────────────────────────────────── */
function CodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(1);
  const [copied, setCopied] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/codes");
      setCodes(data.codes);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setErr("");
    try {
      await api.post("/admin/codes", { count: Number(count) || 1 });
      await load();
    } catch (e) { setErr(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/codes/${id}`); setCodes(codes.filter((c) => c.id !== id)); }
    catch (e) { setErr(errMsg(e)); }
  };
  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(""), 1500);
  };

  const used = codes.filter((c) => String(c.is_used).toLowerCase() === "true").length;

  return (
    <div className="animate-fadeIn">
      <Header title="Access Codes" sub="Single-use invitation codes. Each code registers exactly one account.">
        <input type="number" min={1} max={50} value={count} onChange={(e) => setCount(e.target.value)} className="field w-24 text-center" data-testid="admin-code-count-input" />
        <button onClick={generate} className="btn-primary flex items-center gap-2" data-testid="admin-generate-code-button">
          <Plus size={17} /> Generate
        </button>
        <button onClick={load} className="btn-ghost p-2.5" title="Refresh"><RefreshCw size={16} /></button>
      </Header>

      {err && <div className="mb-4 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3">{err}</div>}

      <div className="grid grid-cols-3 gap-4 mb-7 max-w-xl">
        <Stat label="Total" value={codes.length} />
        <Stat label="Available" value={codes.length - used} color="text-tv-green" />
        <Stat label="Used" value={used} color="text-tv-orange" />
      </div>

      <div className="card overflow-hidden" data-testid="admin-codes-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-tv-muted text-[10px] uppercase tracking-widest">
              <th className="text-left font-bold px-5 py-4">Code</th>
              <th className="text-left font-bold px-5 py-4">Status</th>
              <th className="text-left font-bold px-5 py-4">Used by</th>
              <th className="text-left font-bold px-5 py-4">Internal notes</th>
              <th className="text-right font-bold px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-tv-muted">Loading…</td></tr>
            ) : codes.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-tv-muted">No codes yet. Generate one above.</td></tr>
            ) : (
              codes.map((c) => {
                const isUsed = String(c.is_used).toLowerCase() === "true";
                return (
                  <tr key={c.id} className="border-t border-tv-border hover:bg-tv-hover/50">
                    <td className="px-5 py-3.5 font-mono font-bold tracking-widest text-tv-text">{c.code}</td>
                    <td className="px-5 py-3.5">
                      {isUsed ? (
                        <span className="inline-flex items-center gap-1.5 text-tv-orange text-xs font-bold bg-tv-orange/10 px-2.5 py-1 rounded-lg">Used</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-tv-green text-xs font-bold bg-tv-green/10 px-2.5 py-1 rounded-lg">Available</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-tv-sub">{c.used_by || "-"}</td>
                    <td className="px-5 py-3.5">
                      <NoteCell code={c} onError={setErr} />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => copy(c.code)} className="btn-ghost p-2" title="Copy" data-testid={`copy-code-${c.id}`}>
                          {copied === c.code ? <Check size={15} className="text-tv-green" /> : <Copy size={15} />}
                        </button>
                        <button onClick={() => del(c.id)} className="btn-ghost p-2 hover:!text-tv-red hover:!border-tv-red/40" title="Delete" data-testid={`delete-code-${c.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Users ─────────────────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pwFor, setPwFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data.users); }
    catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    const next = (u.status || "active") === "blocked" ? "active" : "blocked";
    try { await api.put(`/admin/users/${u.id}/status?status=${next}`); setUsers(users.map((x) => x.id === u.id ? { ...x, status: next } : x)); }
    catch (e) { setErr(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try { await api.delete(`/admin/users/${id}`); setUsers(users.filter((u) => u.id !== id)); }
    catch (e) { setErr(errMsg(e)); }
  };

  return (
    <div className="animate-fadeIn">
      {pwFor && <SetPasswordDialog user={pwFor} onClose={() => setPwFor(null)} onError={setErr} />}
      <Header title="Users" sub="Registered accounts. Block or remove access at any time.">
        <button onClick={load} className="btn-ghost p-2.5" title="Refresh"><RefreshCw size={16} /></button>
      </Header>
      {err && <div className="mb-4 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3">{err}</div>}
      <div className="grid grid-cols-2 gap-4 mb-7 max-w-sm">
        <Stat label="Total users" value={users.length} />
        <Stat label="Blocked" value={users.filter((u) => u.status === "blocked").length} color="text-tv-red" />
      </div>

      <div className="card overflow-hidden" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-tv-muted text-[10px] uppercase tracking-widest">
              <th className="text-left font-bold px-5 py-4">Email</th>
              <th className="text-left font-bold px-5 py-4">Username</th>
              <th className="text-left font-bold px-5 py-4">Status</th>
              <th className="text-right font-bold px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-tv-muted">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-tv-muted">No users registered yet.</td></tr>
            ) : (
              users.map((u) => {
                const blocked = (u.status || "active") === "blocked";
                return (
                  <tr key={u.id} className="border-t border-tv-border hover:bg-tv-hover/50">
                    <td className="px-5 py-3.5 text-tv-text font-medium">{u.email}</td>
                    <td className="px-5 py-3.5 text-tv-sub">{u.username || "-"}</td>
                    <td className="px-5 py-3.5">
                      {blocked ? (
                        <span className="text-tv-red text-xs font-bold bg-tv-red/10 px-2.5 py-1 rounded-lg">Blocked</span>
                      ) : (
                        <span className="text-tv-green text-xs font-bold bg-tv-green/10 px-2.5 py-1 rounded-lg">Active</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setPwFor(u)} className="btn-ghost p-2" title="Set password" data-testid={`set-password-${u.id}`}>
                          <KeyRound size={15} />
                        </button>
                        <button onClick={() => toggle(u)} className="btn-ghost p-2" title={blocked ? "Unblock" : "Block"} data-testid={`toggle-user-${u.id}`}>
                          {blocked ? <CheckCircle2 size={15} className="text-tv-green" /> : <Ban size={15} className="text-tv-orange" />}
                        </button>
                        <button onClick={() => del(u.id)} className="btn-ghost p-2 hover:!text-tv-red hover:!border-tv-red/40" title="Delete" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Global Playlists ─────────────────────────────────────── */
function ListsTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", url: "" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/lists"); setLists(data.lists); }
    catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    try { await api.post("/admin/lists", { ...form, list_type: "url" }); setForm({ name: "", url: "" }); await load(); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/lists/${id}`); setLists(lists.filter((l) => l.id !== id)); }
    catch (e) { setErr(errMsg(e)); }
  };

  const globals = lists.filter((l) => String(l.is_default).toLowerCase() === "true");

  return (
    <div className="animate-fadeIn">
      <Header title="Playlists" sub="Global M3U playlists offered to every user by default." />
      {err && <div className="mb-4 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3">{err}</div>}

      <form onSubmit={add} className="card p-5 mb-6 flex flex-col sm:flex-row gap-3 max-w-3xl" data-testid="admin-add-list-form">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Playlist name" className="field sm:w-56" data-testid="admin-list-name-input" />
        <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/playlist.m3u" className="field flex-1" data-testid="admin-list-url-input" />
        <button type="submit" className="btn-primary flex items-center gap-2" data-testid="admin-add-list-button"><Plus size={17} /> Add</button>
      </form>

      <div className="grid gap-3 max-w-3xl" data-testid="admin-lists">
        {loading ? (
          <div className="text-tv-muted">Loading…</div>
        ) : globals.length === 0 ? (
          <div className="card p-8 text-center text-tv-muted">No global playlists yet.</div>
        ) : (
          globals.map((l) => (
            <div key={l.id} className="card px-5 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-bold text-tv-text">{l.name}</div>
                <div className="text-tv-muted text-xs truncate max-w-lg">{l.url}</div>
              </div>
              <button onClick={() => del(l.id)} className="btn-ghost p-2 hover:!text-tv-red hover:!border-tv-red/40" title="Delete" data-testid={`delete-list-${l.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
